import React, { useState, useEffect } from 'react';

const ClientFormPage = ({ clientId, onSave, onCancel }) => {
  const [client, setClient] = useState({
    name: '',
    fiscalCode: '',
    address: '',
    locality: '',
    county: '',
    country: 'România',
    email: '',
    iban: '',
    bank: '',
    representativeName: '',
    representativeFirstName: '',
    representativePosition: '',
    representativeEmail: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEditing = Boolean(clientId);

  useEffect(() => {
    if (isEditing) {
      fetchClient();
    }
  }, [clientId]);

  const fetchClient = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/clients/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setClient(data);
      } else {
        throw new Error('Eroare la încărcarea clientului');
      }
    } catch (error) {
      console.error('Error fetching client:', error);
      alert('Eroare la încărcarea datelor clientului');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClient(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!client.name.trim()) {
      newErrors.name = 'Numele este obligatoriu';
    }

    if (!client.fiscalCode.trim()) {
      newErrors.fiscalCode = 'Codul fiscal este obligatoriu';
    } else if (!/^(RO)?[0-9]{2,10}$/.test(client.fiscalCode.replace(/\s/g, ''))) {
      newErrors.fiscalCode = 'Format cod fiscal invalid';
    }

    if (client.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email)) {
      newErrors.email = 'Format email invalid';
    }

    if (client.representativeEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.representativeEmail)) {
      newErrors.representativeEmail = 'Format email invalid';
    }

    // if (client.iban && !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/.test(client.iban.replace(/\s/g, ''))) {
    //   newErrors.iban = 'Format IBAN invalid';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = isEditing ? `/api/clients/${clientId}` : '/api/clients';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(client),
      });

      if (response.ok) {
        const savedClient = await response.json();
        alert(isEditing ? 'Client actualizat cu succes!' : 'Client creat cu succes!');
        if (onSave) {
          onSave(savedClient);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Eroare la salvarea clientului');
      }
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Eroare la salvarea clientului: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      window.history.back();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Editare Client' : 'Client Nou'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isEditing ? 'Modificați informațiile clientului' : 'Completați informațiile pentru clientul nou'}
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <div onSubmit={handleSubmit} className="space-y-6">
          {/* Informații generale */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informații Generale</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume Client *
                </label>
                <input
                  type="text"
                  name="name"
                  value={client.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Numele companiei"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cod Fiscal *
                </label>
                <input
                  type="text"
                  name="fiscalCode"
                  value={client.fiscalCode}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.fiscalCode ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="RO12345678"
                />
                {errors.fiscalCode && <p className="text-red-500 text-sm mt-1">{errors.fiscalCode}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresă
                </label>
                <input
                  type="text"
                  name="address"
                  value={client.address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Strada, numărul, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Localitate
                </label>
                <input
                  type="text"
                  name="locality"
                  value={client.locality}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="București"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Județ
                </label>
                <input
                  type="text"
                  name="county"
                  value={client.county}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="București"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={client.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="contact@exemplu.ro"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
            </div>
          </div>

          {/* Informații bancare */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informații Bancare</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IBAN
                </label>
                <input
                  type="text"
                  name="iban"
                  value={client.iban}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.iban ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="RO49AAAA1B31007593840000"
                />
                {errors.iban && <p className="text-red-500 text-sm mt-1">{errors.iban}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bancă
                </label>
                <input
                  type="text"
                  name="bank"
                  value={client.bank}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Banca Transilvania"
                />
              </div>
            </div>
          </div>

          {/* Reprezentant */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Reprezentant</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume
                </label>
                <input
                  type="text"
                  name="representativeName"
                  value={client.representativeName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Popescu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prenume
                </label>
                <input
                  type="text"
                  name="representativeFirstName"
                  value={client.representativeFirstName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ion"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Funcție
                </label>
                <input
                  type="text"
                  name="representativePosition"
                  value={client.representativePosition}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Director General"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Reprezentant
                </label>
                <input
                  type="email"
                  name="representativeEmail"
                  value={client.representativeEmail}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.representativeEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ion.popescu@exemplu.ro"
                />
                {errors.representativeEmail && <p className="text-red-500 text-sm mt-1">{errors.representativeEmail}</p>}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {saving ? 'Se salvează...' : (isEditing ? 'Actualizează' : 'Creează')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientFormPage;